import { isCodingSortedByCode, isServerSortedPaidol } from 'util/codingSortSetting';
import sortAlphanumericArrays, { sortCodingOptions, CODING_SORT_FIELDS } from 'util/sortAlphanumericArrays';
import { GraphQLResult } from '@aws-amplify/api-graphql';
import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import {
  AgaveAPInvoicesCreateExpenseMutationVariables,
  AgaveAccount,
  AgaveCostCode,
  AgaveCostType,
  AgaveCreateExpenseMutationVariables,
  AgaveEmployee,
  AgaveError,
  AgaveGlCode,
  AgaveBankAccount,
  GetAgaveBankAccountsQueryVariables,
  AgaveLinkTokenExchangeMutationVariables,
  AgavePassThroughResponse,
  AgaveProject,
  AgaveSubJob,
  AgaveVendor,
  AgaveResolverMutationMutationVariables,
  CreateAgaveExpenseAPInvoiceInput,
  HighnoteTransactionEvent,
  GetAgaveAccountQueryVariables,
  GetAgaveEmployeesQueryVariables,
  GetAgaveVendorByNameQueryVariables,
  PersistAgaveDataMutationVariables,
  PostAgavePassThroughMutationVariables,
  PaidolAgaveSync,
  V2SearchAgaveGLCodesQueryVariables,
  V2SearchAgaveProjectsQueryVariables,
  V2SearchAgaveCostCodesQueryVariables,
  V2SearchAgaveCostTypesQueryVariables,
  V2SearchAgaveSubJobsQueryVariables,
  V2SearchAgaveVendorsQueryVariables,
  GetAgaveAPInvoiceByFilterSageIntacctQueryVariables,
  AgaveAPInvoicesPayload,
  AgavePostJobCostMutationVariables,
  GetAgaveVendorsQueryVariables,
  MigrateERPEndpointSettingsQueryVariables,
  MigrateERPEndpointSettingsPayload,
  AgaveLocation,
  AgaveDepartment,
  AgaveEmployeeEntity,
  AgaveSubaccount,
  AgaveEquipment,
  AgaveEmCostCode,
  AgaveEmCostType,
  V2SearchAgaveLocationsQueryVariables,
  V2SearchAgaveDepartmentsQueryVariables,
  V2SearchAgaveSubaccountsQueryVariables,
  V2SearchAgaveEmployeesQueryVariables,
} from 'API';
// SEE-1435: v2SearchAgaveEquipment exists only on the CDK AppSync API, so its
// arg type comes from CDK codegen. The sibling search vars above are in `API`
// only because the Amplify schema still declares those queries too — a
// duplicate we deliberately did not extend for equipment.
import { QueryV2SearchAgaveEquipmentArgs } from 'CDK_API';
import { RootState } from 'app/store/rootReducer';
import { agaveService } from 'app/services/agave.service';
import { AgaveSourceSystem } from 'app/services/agave/agaveConstants';
import { paidolService } from 'app/services/paidol.service';
import { transactionService } from 'app/services/transaction.service';
import {
  UpdateTransactionArgs,
  applyLocalTransactionUpdates,
  setCodingTypeOverride,
} from './transactionsSlice';
import { showQBDFullName } from 'app/pages/overview/tabs/transactions/utils';

import { jsonToXml } from 'util/helpers';

// Type-safe search type union
export type SearchType =
  | 'projects'
  | 'glCodes'
  | 'costCodes'
  | 'costTypes'
  | 'vendors'
  | 'subJobs'
  | 'locations'
  | 'departments'
  | 'employees'
  | 'subaccounts'
  | 'equipment'
  | 'emCostCodes'
  | 'emCostTypes';

// Initialization status enum
export type InitializationStatus = 'idle' | 'pending' | 'initialized';

// Consolidated initialization state interface
export interface SearchInitializationState {
  status: InitializationStatus;
  dropdownIds: string[];
}

// SEE-690 migration helper: the AgaveService returns CDK_API types, but the
// Redux state shape was originally typed against Amplify-generated API.ts types.
// During the coexistence window we bridge with this named cast so the shim is
// loud (greppable as `asAmplifyType`) and removable in one place once the
// Redux state types are migrated off API.ts.
//
// Safe for fields the codebase currently reads. The two schemas are kept in
// sync manually and CI snapshots catch drift on the fields we exercise today.
// Known shape divergence — DO NOT reach for these from CDK-path callers without
// adapting:
//   - `AgavePassThroughResponse.headers`: Amplify wraps in `AWSJSONWrapper`
//     (`.raw: AWSJSON`); CDK returns raw `AWSJSON` directly.
//   - `AgaveResolverMutationResponse.headers`: same shape mismatch.
// No current callers access `.headers.raw`, but a future caller typing against
// the Amplify shape would silently get `undefined` on the new CDK path.
const asAmplifyType = <U>(value: unknown): U => value as U;

// Generic search result validation function
const validateSearchResult = <T>(
  data: any,
  itemValidator?: (item: any) => item is T
): {
  items: T[];
  nextToken: string | null;
  total: number;
} | null => {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const { items, nextToken, total } = data;

  if (!Array.isArray(items)) {
    return null;
  }

  // If an item validator is provided, validate each item
  if (itemValidator && !items.every(itemValidator)) {
    return null;
  }

  return {
    items: items as T[],
    nextToken: nextToken ?? null,
    total: typeof total === 'number' ? total : items.length,
  };
};

const validateSearchProjectsResult = (
  data: any
): {
  items: AgaveProject[];
  nextToken: string | null;
  total: number;
} | null => {
  return validateSearchResult<AgaveProject>(data);
};

const validateSearchGlCodesResult = (
  data: any
): {
  items: AgaveGlCode[];
  nextToken: string | null;
  total: number;
} | null => {
  return validateSearchResult<AgaveGlCode>(data);
};

const validateSearchCostCodesResult = (
  data: any
): {
  items: AgaveCostCode[];
  nextToken: string | null;
  total: number;
} | null => {
  return validateSearchResult<AgaveCostCode>(data);
};

const validateSearchCostTypesResult = (
  data: any
): {
  items: AgaveCostType[];
  nextToken: string | null;
  total: number;
} | null => {
  return validateSearchResult<AgaveCostType>(data);
};

const validateSearchVendorsResult = (
  data: any
): {
  items: AgaveVendor[];
  nextToken: string | null;
  total: number;
} | null => {
  return validateSearchResult<AgaveVendor>(data);
};

const validateSearchSubJobsResult = (
  data: any
): {
  items: AgaveSubJob[];
  nextToken: string | null;
  total: number;
} | null => {
  return validateSearchResult<AgaveSubJob>(data);
};

const validateSearchLocationsResult = (
  data: any
): {
  items: AgaveLocation[];
  nextToken: string | null;
  total: number;
} | null => {
  return validateSearchResult<AgaveLocation>(data);
};

const validateSearchDepartmentsResult = (
  data: any
): {
  items: AgaveDepartment[];
  nextToken: string | null;
  total: number;
} | null => {
  return validateSearchResult<AgaveDepartment>(data);
};

const validateSearchSubaccountsResult = (
  data: any
): {
  items: AgaveSubaccount[];
  nextToken: string | null;
  total: number;
} | null => {
  return validateSearchResult<AgaveSubaccount>(data);
};

const validateSearchEquipmentResult = (
  data: any
): {
  items: AgaveEquipment[];
  nextToken: string | null;
  total: number;
} | null => {
  return validateSearchResult<AgaveEquipment>(data);
};

const validateSearchEmployeesResult = (
  data: any
): {
  items: AgaveEmployeeEntity[];
  nextToken: string | null;
  total: number;
} | null => {
  return validateSearchResult<AgaveEmployeeEntity>(data);
};

const validateArrayResult = (data: any): any[] | null => {
  if (!Array.isArray(data)) {
    return null;
  }
  return data;
};

export interface AgaveState {
  agaveLoading: boolean;
  loadingMoreGlCodes: boolean;
  loadingMoreProjects: boolean;
  loadingMoreCostCodes: boolean;
  loadingMoreCostTypes: boolean;
  loadingMoreVendors: boolean;
  loadingMoreSubJobs: boolean;
  loadingMoreLocations: boolean;
  loadingMoreDepartments: boolean;
  loadingMoreSubaccounts: boolean;
  loadingMoreEquipment: boolean;
  loadingMoreEmCostCodes: boolean;
  loadingMoreEmCostTypes: boolean;
  loadingMoreEmployees: boolean;
  // Loading states for search actions to prevent concurrent dispatches
  searchingProjects: boolean;
  searchingGlCodes: boolean;
  searchingCostCodes: boolean;
  searchingCostTypes: boolean;
  searchingVendors: boolean;
  searchingSubJobs: boolean;
  searchingLocations: boolean;
  searchingDepartments: boolean;
  searchingSubaccounts: boolean;
  searchingEquipment: boolean;
  searchingEmCostCodes: boolean;
  searchingEmCostTypes: boolean;
  searchingEmployees: boolean;
  linkToken: string | undefined;
  accountToken: string | undefined;
  selectedProjectId: string | undefined;
  agaveProjects: AgaveProject[] | undefined;
  searchProjects:
    | {
        items: AgaveProject[];
        nextToken: string | null;
        total: number;
      }
    | undefined;
  projectEmployees: AgaveEmployee[] | undefined;
  projectCostCodes: AgaveCostCode[] | undefined;
  costTypes: AgaveCostType[] | undefined;
  agaveAccount: AgaveAccount | undefined;
  glCodes: AgaveGlCode[] | undefined;
  searchGlCodes:
    | {
        items: AgaveGlCode[];
        nextToken: string | null;
        total: number;
      }
    | undefined;
  searchCostCodes:
    | {
        items: AgaveCostCode[];
        nextToken: string | null;
        total: number;
      }
    | undefined;
  searchCostTypes:
    | {
        items: AgaveCostType[];
        nextToken: string | null;
        total: number;
      }
    | undefined;
  searchVendors:
    | {
        items: AgaveVendor[];
        nextToken: string | null;
        total: number;
      }
    | undefined;
  searchSubJobs:
    | {
        items: AgaveSubJob[];
        nextToken: string | null;
        total: number;
      }
    | undefined;
  searchLocations:
    | {
        items: AgaveLocation[];
        nextToken: string | null;
        total: number;
      }
    | undefined;
  searchDepartments:
    | {
        items: AgaveDepartment[];
        nextToken: string | null;
        total: number;
      }
    | undefined;
  searchSubaccounts:
    | {
        items: AgaveSubaccount[];
        nextToken: string | null;
        total: number;
      }
    | undefined;
  searchEquipment:
    | {
        items: AgaveEquipment[];
        nextToken: string | null;
        total: number;
      }
    | undefined;
  searchEmCostCodes:
    | {
        items: AgaveEmCostCode[];
        nextToken: string | null;
        total: number;
      }
    | undefined;
  searchEmCostTypes:
    | {
        items: AgaveEmCostType[];
        nextToken: string | null;
        total: number;
      }
    | undefined;
  glCodesByCosting: Record<string, AgaveGlCode[]>;
  loadingGlCodesByCosting: boolean;
  searchEmployees:
    | {
        items: AgaveEmployeeEntity[];
        nextToken: string | null;
        total: number;
      }
    | undefined;
  bankAccounts: AgaveBankAccount[] | undefined;
  vendors: AgaveVendor[] | undefined;
  lastSync: PaidolAgaveSync | undefined;
  // Consolidated initialization tracking per search type
  searchInitialization: {
    projects: SearchInitializationState;
    glCodes: SearchInitializationState;
    costCodes: SearchInitializationState;
    costTypes: SearchInitializationState;
    vendors: SearchInitializationState;
    subJobs: SearchInitializationState;
    locations: SearchInitializationState;
    departments: SearchInitializationState;
    subaccounts: SearchInitializationState;
    equipment: SearchInitializationState;
    emCostCodes: SearchInitializationState;
    emCostTypes: SearchInitializationState;
    employees: SearchInitializationState;
  };
  // SEE-1384: in-flight updateTransactionAgaveClient saves per transaction id.
  // The sidesheet disables Post while > 0 so a post can't race a just-made
  // coding change and read the stale transaction server-side.
  pendingCodingSaves: Record<string, number>;
}

/**
 * SEE-1325: has this org opted into ordering coding dropdowns by code?
 *
 * Read from the userCompanies slice because the setting is a paidol setting, and
 * slice reducers cannot reach across slices — the thunks resolve it via
 * `getState()` and hand the reducers an already-ordered list.
 */
const sortsCodingByCode = (state: unknown): boolean => {
  const root = state as RootState;
  return isCodingSortedByCode(
    root.userCompanies?.selectedPaidol,
    // Account slug as fallback for paidols without agaveSourceSystemSlug —
    // the toggle's visibility gate falls back the same way.
    root.cards?.agave?.agaveAccount?.source_system?.slug
  );
};

/**
 * SEE-1325 / SEE-1622: is this paidol's coding server-ordered? Every direct-ERP
 * paidol is, in BOTH toggle states — the resolvers sort the whole set (by name
 * when the toggle is off, by code when on) and offset-paginate it, so pages
 * must be stored verbatim. The toggle picks the FIELD; being on a direct ERP is
 * what turns server ordering on.
 */
const serverOrdersCoding = (state: unknown): boolean => {
  const root = state as RootState;
  return isServerSortedPaidol(
    root.userCompanies?.selectedPaidol,
    root.cards?.agave?.agaveAccount?.source_system?.slug
  );
};

/**
 * SEE-1325: order a coding list for the store.
 *
 * When `serverOrdered` is true (direct Vista, either toggle state) the SERVER
 * already ordered the whole set — name when the toggle is off, code when on —
 * so pages are stored VERBATIM. Re-sorting client-side would fight the
 * server's order wherever the two natural sorts disagree (the JS sorter
 * splits '116B' into 116+'B' and compares '20-001'/'20-010' on their
 * separator text; the backend treats whole alphanumeric runs as text and
 * ignores separators) — that disagreement is what made load-more re-shuffle
 * rows above the viewport. Otherwise the server pages are unordered and the
 * client name sort applies, as it has since SEE-346.
 */
const orderCodingItems = <T extends Record<string, unknown>>(
  items: T[],
  serverOrdered: boolean | undefined,
  fields: { codeField: string; nameField: string } | undefined
): T[] => (serverOrdered ? items : sortCodingOptions(items, { byCode: false, ...fields }));

/**
 * SEE-1622: the same rule for the lists SEE-1622 added to server ordering —
 * sub-jobs, locations, departments, sub-accounts and employees. Server-ordered
 * pages are stored verbatim; otherwise each reducer's existing per-page sort
 * applies unchanged (its own field, blanks first), so Agave and no-ERP paidols
 * behave exactly as before. Not `orderCodingItems`: its fallback sorts blanks
 * last, which would quietly reorder those paidols' lists.
 */
const keepServerOrder = <T extends Record<string, unknown>>(
  items: T[],
  serverOrdered: boolean | undefined,
  field: string
): T[] => (serverOrdered ? items : sortAlphanumericArrays(items, field));

const initialState: AgaveState = {
  agaveAccount: undefined,
  agaveLoading: false,
  loadingMoreGlCodes: false,
  loadingMoreProjects: false,
  loadingMoreCostCodes: false,
  loadingMoreCostTypes: false,
  loadingMoreVendors: false,
  loadingMoreSubJobs: false,
  loadingMoreLocations: false,
  loadingMoreDepartments: false,
  loadingMoreSubaccounts: false,
  loadingMoreEquipment: false,
  loadingMoreEmCostCodes: false,
  loadingMoreEmCostTypes: false,
  loadingMoreEmployees: false,
  searchingProjects: false,
  searchingGlCodes: false,
  searchingCostCodes: false,
  searchingCostTypes: false,
  searchingVendors: false,
  searchingSubJobs: false,
  searchingLocations: false,
  searchingDepartments: false,
  searchingSubaccounts: false,
  searchingEquipment: false,
  searchingEmCostCodes: false,
  searchingEmCostTypes: false,
  searchingEmployees: false,
  linkToken: undefined,
  accountToken: undefined,
  selectedProjectId: undefined,
  agaveProjects: undefined,
  searchProjects: undefined,
  projectEmployees: undefined,
  projectCostCodes: undefined,
  costTypes: undefined,
  glCodes: undefined,
  searchGlCodes: undefined,
  searchCostCodes: undefined,
  searchCostTypes: undefined,
  searchVendors: undefined,
  searchSubJobs: undefined,
  searchLocations: undefined,
  searchDepartments: undefined,
  searchSubaccounts: undefined,
  searchEquipment: undefined,
  searchEmCostCodes: undefined,
  searchEmCostTypes: undefined,
  glCodesByCosting: {},
  loadingGlCodesByCosting: false,
  searchEmployees: undefined,
  bankAccounts: undefined,
  vendors: undefined,
  lastSync: undefined,
  searchInitialization: {
    projects: { status: 'idle', dropdownIds: [] },
    glCodes: { status: 'idle', dropdownIds: [] },
    costCodes: { status: 'idle', dropdownIds: [] },
    costTypes: { status: 'idle', dropdownIds: [] },
    vendors: { status: 'idle', dropdownIds: [] },
    subJobs: { status: 'idle', dropdownIds: [] },
    locations: { status: 'idle', dropdownIds: [] },
    departments: { status: 'idle', dropdownIds: [] },
    subaccounts: { status: 'idle', dropdownIds: [] },
    equipment: { status: 'idle', dropdownIds: [] },
    emCostCodes: { status: 'idle', dropdownIds: [] },
    emCostTypes: { status: 'idle', dropdownIds: [] },
    employees: { status: 'idle', dropdownIds: [] },
  },
  pendingCodingSaves: {},
};

export const syncAgave = createAsyncThunk(
  'agave/syncAgave',
  async (input: PersistAgaveDataMutationVariables, { rejectWithValue }) => {
    try {
      return await agaveService.persistAgaveData(input);
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const fetchLastFinishedSyncByPaidolId = createAsyncThunk(
  'agave/getLastFinishedSyncByPaidolId',
  async (paidolId: string, { rejectWithValue }) => {
    try {
      return await agaveService.getAgaveSyncByPaidolId({
        input: { paidolId, finished: true },
      });
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const fetchLastSyncByPaidolId = createAsyncThunk(
  'agave/getLastSyncByPaidolId',
  async (paidolId: string, { rejectWithValue }) => {
    try {
      const result = await agaveService.getAgaveSyncByPaidolId({ input: { paidolId } });
      return result?.sync;
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const getAgaveLinkToken = createAsyncThunk(
  'agave/getAgaveLinkToken',
  async (_, { rejectWithValue }) => {
    try {
      const result = await agaveService.agaveLinkTokenCreate();
      return result?.link_token;
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const getPersistedAgaveAccountToken = createAsyncThunk(
  'agave/getPersistedAgaveAccountToken',
  async (id: string, { rejectWithValue }) => {
    // SEE-1232 row n: transport flip only — same argument, same resolved value.
    try {
      return await paidolService.getPaidolAgaveToken(id);
    } catch (error) {
      // Preserved from the Amplify path: rejection carries the GraphQL errors when the
      // transport supplies them, so the existing `rejected` case is unchanged.
      const errors = (error as GraphQLResult<unknown>)?.errors;
      return rejectWithValue(errors ?? error);
    }
  }
);

export const getAgaveAccountToken = createAsyncThunk(
  'agave/getAgaveAccountToken',
  async ({ input }: AgaveLinkTokenExchangeMutationVariables, { rejectWithValue }) => {
    try {
      const agaveAccount = await agaveService.agaveLinkTokenExchange({ input });
      if (!agaveAccount?.account_token) {
        throw new Error('Invalid agave account token response');
      }
      return agaveAccount;
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const searchProjects = createAsyncThunk(
  'agave/searchProjects',
  async (
    input: V2SearchAgaveProjectsQueryVariables & { dropdownId?: string },
    { getState, rejectWithValue }
  ) => {
    const { dropdownId, ...searchInput } = input;
    try {
      const result = await agaveService.v2SearchAgaveProjects(searchInput);
      const validatedResult = validateSearchProjectsResult(result);
      if (!validatedResult) {
        throw new Error('Invalid search projects result structure');
      }
      // SEE-1325: the reducer sorts but cannot reach the paidol setting, so
      // resolve it here and hand the order down with the payload.
      return { ...validatedResult, dropdownId, serverOrdered: serverOrdersCoding(getState()) };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  },
  {
    // Add condition function to prevent duplicate requests
    condition: (input, { getState }) => {
      const state = getState() as RootState;
      const agaveState = state?.cards?.agave;

      // Don't dispatch if already searching or if already initialized
      if (
        agaveState?.searchingProjects ||
        agaveState?.searchInitialization?.projects?.status === 'pending' ||
        agaveState?.searchInitialization?.projects?.status === 'initialized'
      ) {
        return false; // This will prevent the thunk from being dispatched
      }

      return true; // Allow the thunk to be dispatched
    },
  }
);

export const loadMoreProjects = createAsyncThunk(
  'agave/loadMoreProjects',
  async (input: V2SearchAgaveProjectsQueryVariables, { rejectWithValue, getState }) => {
    try {
      const result = await agaveService.v2SearchAgaveProjects(input);
      const validatedResult = validateSearchProjectsResult(result);
      if (!validatedResult) {
        throw new Error('Invalid load more projects result structure');
      }
      return { ...validatedResult, serverOrdered: serverOrdersCoding(getState()) };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const getGlCodes = createAsyncThunk(
  'agave/getGlCodes',
  async (input: { paidolId: string; limit?: number }, { rejectWithValue }) => {
    try {
      const result = await agaveService.listAgaveGlCodesByPaidolId({
        input: { paidolId: input.paidolId, limit: input.limit },
      });
      const validatedResult = validateArrayResult(result.items);
      if (!validatedResult) {
        throw new Error('Invalid GL codes result structure');
      }
      return validatedResult as AgaveGlCode[];
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const searchGlCodes = createAsyncThunk(
  'agave/searchGlCodes',
  async (
    input: V2SearchAgaveGLCodesQueryVariables & { dropdownId?: string },
    { getState, rejectWithValue }
  ) => {
    const { dropdownId, ...searchInput } = input;
    try {
      const result = await agaveService.v2SearchAgaveGLCodes(searchInput);
      const validatedResult = validateSearchGlCodesResult(result);
      if (!validatedResult) {
        throw new Error('Invalid search GL codes result structure');
      }
      // SEE-1325: as above — resolve the setting here, sort in the reducer.
      return { ...validatedResult, dropdownId, serverOrdered: serverOrdersCoding(getState()) };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  },
  {
    // Add condition function to prevent duplicate requests
    condition: (input, { getState }) => {
      const state = getState() as RootState;
      const agaveState = state?.cards?.agave;

      // Don't dispatch if already searching or if already initialized
      if (
        agaveState?.searchingGlCodes ||
        agaveState?.searchInitialization?.glCodes?.status === 'pending' ||
        agaveState?.searchInitialization?.glCodes?.status === 'initialized'
      ) {
        return false; // This will prevent the thunk from being dispatched
      }

      return true; // Allow the thunk to be dispatched
    },
  }
);

export const loadMoreGlCodes = createAsyncThunk(
  'agave/loadMoreGlCodes',
  async (input: V2SearchAgaveGLCodesQueryVariables, { rejectWithValue, getState }) => {
    try {
      const result = await agaveService.v2SearchAgaveGLCodes(input);
      const validatedResult = validateSearchGlCodesResult(result);
      if (!validatedResult) {
        throw new Error('Invalid load more GL codes result structure');
      }
      return { ...validatedResult, serverOrdered: serverOrdersCoding(getState()) };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const getGlCodesByCosting = createAsyncThunk(
  'agave/getGlCodesByCosting',
  async (
    input: { paidolId: string; projectId: string; costCodeId: string; costTypeId: string },
    { rejectWithValue }
  ) => {
    try {
      const result = await agaveService.v2GetAgaveGlCodesByCosting({ input });
      // SEE-1390: itemSourceId/itemName ride the same payload (typed via cast
      // until codegen regenerates GetAgaveGlCodesByCostingPayload).
      const withItem = result as typeof result & {
        itemSourceId?: string | null;
        itemName?: string | null;
      };
      return {
        key: `${input.projectId}#${input.costCodeId}#${input.costTypeId}`,
        items: (result?.items ?? []) as AgaveGlCode[],
        itemSourceId: withItem?.itemSourceId ?? null,
        itemName: withItem?.itemName ?? null,
      };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const searchVendors = createAsyncThunk(
  'agave/searchVendors',
  async (
    input: V2SearchAgaveVendorsQueryVariables & { dropdownId?: string },
    { rejectWithValue, getState }
  ) => {
    const { dropdownId, ...searchInput } = input;
    try {
      const result = await agaveService.v2SearchAgaveVendors(searchInput);
      const validatedResult = validateSearchVendorsResult(result);
      if (!validatedResult) {
        throw new Error('Invalid search vendors result structure');
      }
      return { ...validatedResult, dropdownId, serverOrdered: serverOrdersCoding(getState()) };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  },
  {
    // Add condition function to prevent duplicate requests
    condition: (input, { getState }) => {
      const state = getState() as RootState;
      const agaveState = state?.cards?.agave;

      // Don't dispatch if already searching or if already initialized
      if (
        agaveState?.searchingVendors ||
        agaveState?.searchInitialization?.vendors?.status === 'pending' ||
        agaveState?.searchInitialization?.vendors?.status === 'initialized'
      ) {
        return false; // This will prevent the thunk from being dispatched
      }

      return true; // Allow the thunk to be dispatched
    },
  }
);

export const loadMoreVendors = createAsyncThunk(
  'agave/loadMoreVendors',
  async (input: V2SearchAgaveVendorsQueryVariables, { rejectWithValue, getState }) => {
    try {
      const result = await agaveService.v2SearchAgaveVendors(input);
      const validatedResult = validateSearchVendorsResult(result);
      if (!validatedResult) {
        throw new Error('Invalid load more vendors result structure');
      }
      return { ...validatedResult, serverOrdered: serverOrdersCoding(getState()) };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const searchLocations = createAsyncThunk(
  'agave/searchLocations',
  async (
    input: V2SearchAgaveLocationsQueryVariables & { dropdownId?: string },
    { rejectWithValue, getState }
  ) => {
    const { dropdownId, ...searchInput } = input;
    try {
      const result = await agaveService.v2SearchAgaveLocations(searchInput);
      const validatedResult = validateSearchLocationsResult(result);
      if (!validatedResult) {
        throw new Error('Invalid search locations result structure');
      }
      return { ...validatedResult, dropdownId, serverOrdered: serverOrdersCoding(getState()) };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  },
  {
    condition: (input, { getState }) => {
      const state = getState() as RootState;
      const agaveState = state?.cards?.agave;

      if (
        agaveState?.searchingLocations ||
        agaveState?.searchInitialization?.locations?.status === 'pending' ||
        agaveState?.searchInitialization?.locations?.status === 'initialized'
      ) {
        return false;
      }
      return true;
    },
  }
);

export const loadMoreLocations = createAsyncThunk(
  'agave/loadMoreLocations',
  async (input: V2SearchAgaveLocationsQueryVariables, { rejectWithValue, getState }) => {
    try {
      const result = await agaveService.v2SearchAgaveLocations(input);
      const validatedResult = validateSearchLocationsResult(result);
      if (!validatedResult) {
        throw new Error('Invalid load more locations result structure');
      }
      return { ...validatedResult, serverOrdered: serverOrdersCoding(getState()) };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const searchDepartments = createAsyncThunk(
  'agave/searchDepartments',
  async (
    input: V2SearchAgaveDepartmentsQueryVariables & { dropdownId?: string },
    { rejectWithValue, getState }
  ) => {
    const { dropdownId, ...searchInput } = input;
    try {
      const result = await agaveService.v2SearchAgaveDepartments(searchInput);
      const validatedResult = validateSearchDepartmentsResult(result);
      if (!validatedResult) {
        throw new Error('Invalid search departments result structure');
      }
      return { ...validatedResult, dropdownId, serverOrdered: serverOrdersCoding(getState()) };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  },
  {
    condition: (input, { getState }) => {
      const state = getState() as RootState;
      const agaveState = state?.cards?.agave;

      if (
        agaveState?.searchingDepartments ||
        agaveState?.searchInitialization?.departments?.status === 'pending' ||
        agaveState?.searchInitialization?.departments?.status === 'initialized'
      ) {
        return false;
      }
      return true;
    },
  }
);

export const loadMoreDepartments = createAsyncThunk(
  'agave/loadMoreDepartments',
  async (input: V2SearchAgaveDepartmentsQueryVariables, { rejectWithValue, getState }) => {
    try {
      const result = await agaveService.v2SearchAgaveDepartments(input);
      const validatedResult = validateSearchDepartmentsResult(result);
      if (!validatedResult) {
        throw new Error('Invalid load more departments result structure');
      }
      return { ...validatedResult, serverOrdered: serverOrdersCoding(getState()) };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const searchSubaccounts = createAsyncThunk(
  'agave/searchSubaccounts',
  async (
    input: V2SearchAgaveSubaccountsQueryVariables & { dropdownId?: string },
    { rejectWithValue, getState }
  ) => {
    const { dropdownId, ...searchInput } = input;
    try {
      const result = await agaveService.v2SearchAgaveSubaccounts(searchInput);
      const validatedResult = validateSearchSubaccountsResult(result);
      if (!validatedResult) {
        throw new Error('Invalid search subaccounts result structure');
      }
      return { ...validatedResult, dropdownId, serverOrdered: serverOrdersCoding(getState()) };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  },
  {
    condition: (input, { getState }) => {
      const state = getState() as RootState;
      const agaveState = state?.cards?.agave;

      if (
        agaveState?.searchingSubaccounts ||
        agaveState?.searchInitialization?.subaccounts?.status === 'pending' ||
        agaveState?.searchInitialization?.subaccounts?.status === 'initialized'
      ) {
        return false;
      }
      return true;
    },
  }
);

export const loadMoreSubaccounts = createAsyncThunk(
  'agave/loadMoreSubaccounts',
  async (input: V2SearchAgaveSubaccountsQueryVariables, { rejectWithValue, getState }) => {
    try {
      const result = await agaveService.v2SearchAgaveSubaccounts(input);
      const validatedResult = validateSearchSubaccountsResult(result);
      if (!validatedResult) {
        throw new Error('Invalid load more subaccounts result structure');
      }
      return { ...validatedResult, serverOrdered: serverOrdersCoding(getState()) };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

// SEE-1435: equipment search. Replaces the client-side fetch-all that capped
// the dropdown at 500 rows (Griffith has 3,679 pieces of equipment).
export const searchEquipment = createAsyncThunk(
  'agave/searchEquipment',
  async (input: QueryV2SearchAgaveEquipmentArgs & { dropdownId?: string }, { rejectWithValue, getState }) => {
    const { dropdownId, ...searchInput } = input;
    try {
      const result = await agaveService.v2SearchAgaveEquipment(searchInput);
      const validatedResult = validateSearchEquipmentResult(result);
      if (!validatedResult) {
        throw new Error('Invalid search equipment result structure');
      }
      return { ...validatedResult, dropdownId, serverOrdered: serverOrdersCoding(getState()) };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  },
  {
    condition: (input, { getState }) => {
      const state = getState() as RootState;
      const agaveState = state?.cards?.agave;

      if (
        agaveState?.searchingEquipment ||
        agaveState?.searchInitialization?.equipment?.status === 'pending' ||
        agaveState?.searchInitialization?.equipment?.status === 'initialized'
      ) {
        return false;
      }
      return true;
    },
  }
);

export const loadMoreEquipment = createAsyncThunk(
  'agave/loadMoreEquipment',
  async (input: QueryV2SearchAgaveEquipmentArgs, { rejectWithValue, getState }) => {
    try {
      const result = await agaveService.v2SearchAgaveEquipment(input);
      const validatedResult = validateSearchEquipmentResult(result);
      if (!validatedResult) {
        throw new Error('Invalid load more equipment result structure');
      }
      return { ...validatedResult, serverOrdered: serverOrdersCoding(getState()) };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const searchEmployees = createAsyncThunk(
  'agave/searchEmployees',
  async (
    input: V2SearchAgaveEmployeesQueryVariables & { dropdownId?: string },
    { rejectWithValue, getState }
  ) => {
    const { dropdownId, ...searchInput } = input;
    try {
      const result = await agaveService.v2SearchAgaveEmployees(searchInput);
      const validatedResult = validateSearchEmployeesResult(result);
      if (!validatedResult) {
        throw new Error('Invalid search employees result structure');
      }
      return { ...validatedResult, dropdownId, serverOrdered: serverOrdersCoding(getState()) };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  },
  {
    // Only block if already searching (prevent concurrent duplicate requests)
    // Allow re-fetching even if initialized to support dropdown refresh on open
    condition: (input, { getState }) => {
      const state = getState() as RootState;
      const agaveState = state?.cards?.agave;

      if (agaveState?.searchingEmployees) {
        return false; // This will prevent the thunk from being dispatched
      }

      return true; // Allow the thunk to be dispatched
    },
  }
);

export const loadMoreEmployees = createAsyncThunk(
  'agave/loadMoreEmployees',
  async (input: V2SearchAgaveEmployeesQueryVariables, { rejectWithValue, getState }) => {
    try {
      const result = await agaveService.v2SearchAgaveEmployees(input);
      const validatedResult = validateSearchEmployeesResult(result);
      if (!validatedResult) {
        throw new Error('Invalid load more employees result structure');
      }
      return { ...validatedResult, serverOrdered: serverOrdersCoding(getState()) };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const getCostCodes = createAsyncThunk(
  'agave/getCostCodes',
  async (input: { paidolId: string; limit?: number }, { rejectWithValue, getState }) => {
    try {
      const result = await agaveService.listAgaveCostCodesByPaidolId({
        input: { paidolId: input.paidolId, limit: input.limit },
      });
      const validatedResult = validateArrayResult(result.items);
      if (!validatedResult) {
        throw new Error('Invalid cost codes result structure');
      }
      // SEE-1325: this list arrives in raw DynamoDB order and nothing sorted it
      // — SEE-346 covered the search* reducers and missed the get* ones, so the
      // project-scoped cost code dropdown has never been ordered at all. Sorted
      // here rather than in the reducer because the org's choice lives in
      // another slice. The query fetches the whole project-scoped set, so this
      // is a complete sort, not a per-page one.
      //
      // Server-sorted (direct-ERP) paidols only, like every other sort: this
      // thunk is dispatched from useFetchAgaveCommonData (Layout), so an
      // ungated sort would reorder Agave and no-ERP paidols too. SEE-1622:
      // direct Sage Intacct can overlay a per-project name on the label at
      // render time (SEE-1154), so where a code's names diverge across
      // projects this sorts on the stored name — rare (3 of 175 codes in the
      // gordenv Intacct account, all QA edits), checked in QA.
      const root = getState() as RootState;
      const paidol = root.userCompanies?.selectedPaidol;
      const fallbackSlug = root.cards?.agave?.agaveAccount?.source_system?.slug;
      if (!isServerSortedPaidol(paidol, fallbackSlug)) {
        return validatedResult as AgaveCostCode[];
      }
      return sortCodingOptions(validatedResult as AgaveCostCode[], {
        // Spread the shared entry rather than relying on sortCodingOptions'
        // defaults happening to match it — the map is the one source for
        // which fields each entity orders on.
        ...CODING_SORT_FIELDS.costCodes,
        byCode: isCodingSortedByCode(paidol, fallbackSlug),
      }) as AgaveCostCode[];
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const searchCostCodes = createAsyncThunk(
  'agave/searchCostCodes',
  async (
    input: V2SearchAgaveCostCodesQueryVariables & { dropdownId?: string },
    { rejectWithValue, getState }
  ) => {
    const { dropdownId, ...searchInput } = input;
    try {
      const result = await agaveService.v2SearchAgaveCostCodes(searchInput);
      const validatedResult = validateSearchCostCodesResult(result);
      if (!validatedResult) {
        throw new Error('Invalid search cost codes result structure');
      }
      return { ...validatedResult, dropdownId, serverOrdered: serverOrdersCoding(getState()) };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  },
  {
    // Add condition function to prevent only truly duplicate concurrent requests
    condition: (input, { getState }) => {
      const state = getState() as RootState;
      const agaveState = state?.cards?.agave;

      // Only block if already searching (prevent concurrent duplicate requests)
      // Allow re-fetching even if initialized to support dropdown refresh on open
      if (agaveState?.searchingCostCodes) {
        return false; // This will prevent the thunk from being dispatched
      }

      return true; // Allow the thunk to be dispatched
    },
  }
);

export const loadMoreCostCodes = createAsyncThunk(
  'agave/loadMoreCostCodes',
  async (input: V2SearchAgaveCostCodesQueryVariables, { rejectWithValue, getState }) => {
    try {
      const result = await agaveService.v2SearchAgaveCostCodes(input);
      const validatedResult = validateSearchCostCodesResult(result);
      if (!validatedResult) {
        throw new Error('Invalid load more cost codes result structure');
      }
      return { ...validatedResult, serverOrdered: serverOrdersCoding(getState()) };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const searchCostTypes = createAsyncThunk(
  'agave/searchCostTypes',
  async (
    input: V2SearchAgaveCostTypesQueryVariables & { dropdownId?: string },
    { rejectWithValue, getState }
  ) => {
    const { dropdownId, ...originalInput } = input;

    // Both Sage 300 CRE and Sage Intacct support cost type filtering by projectId/costCodeId via join table
    // Strip these filters only for non-Sage systems
    const state = getState() as RootState;
    const agaveAccount = state?.cards?.agave?.agaveAccount;
    const isSageClient =
      agaveAccount?.source_system?.slug === 'sage-300-cre' ||
      agaveAccount?.source_system?.slug === 'sage-intacct';

    // Strip projectId and costCodeId from filter if not a Sage client
    let searchInput = originalInput;
    if (!isSageClient && searchInput?.input?.filter) {
      const { projectId, costCodeId, ...restFilter } = searchInput.input.filter;
      searchInput = {
        ...searchInput,
        input: {
          ...searchInput.input,
          filter: restFilter,
        },
      };
    }

    try {
      const result = await agaveService.v2SearchAgaveCostTypes(searchInput);
      const validatedResult = validateSearchCostTypesResult(result);
      if (!validatedResult) {
        throw new Error('Invalid search cost types result structure');
      }
      return { ...validatedResult, dropdownId, serverOrdered: serverOrdersCoding(getState()) };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  },
  {
    // Add condition function to prevent duplicate requests
    condition: (input, { getState }) => {
      const state = getState() as RootState;
      const agaveState = state?.cards?.agave;

      // Don't dispatch if already searching or if already initialized
      if (
        agaveState?.searchingCostTypes ||
        agaveState?.searchInitialization?.costTypes?.status === 'pending' ||
        agaveState?.searchInitialization?.costTypes?.status === 'initialized'
      ) {
        return false; // This will prevent the thunk from being dispatched
      }

      return true; // Allow the thunk to be dispatched
    },
  }
);

export const loadMoreCostTypes = createAsyncThunk(
  'agave/loadMoreCostTypes',
  async (input: V2SearchAgaveCostTypesQueryVariables, { rejectWithValue, getState }) => {
    // Both Sage 300 CRE and Sage Intacct support cost type filtering by projectId/costCodeId via join table
    // Strip these filters only for non-Sage systems
    const state = getState() as RootState;
    const agaveAccount = state?.cards?.agave?.agaveAccount;
    const isSageClient =
      agaveAccount?.source_system?.slug === 'sage-300-cre' ||
      agaveAccount?.source_system?.slug === 'sage-intacct';

    let searchInput = input;
    // Strip projectId and costCodeId from filter if not a Sage client
    if (!isSageClient && searchInput?.input?.filter) {
      const { projectId, costCodeId, ...restFilter } = searchInput.input.filter;
      searchInput = {
        ...searchInput,
        input: {
          ...searchInput.input,
          filter: restFilter,
        },
      };
    }

    try {
      const result = await agaveService.v2SearchAgaveCostTypes(searchInput);
      const validatedResult = validateSearchCostTypesResult(result);
      if (!validatedResult) {
        throw new Error('Invalid load more cost types result structure');
      }
      return { ...validatedResult, serverOrdered: serverOrdersCoding(getState()) };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const searchSubJobs = createAsyncThunk(
  'agave/searchSubJobs',
  async (
    input: V2SearchAgaveSubJobsQueryVariables & { dropdownId?: string },
    { rejectWithValue, getState }
  ) => {
    const { dropdownId, ...searchInput } = input;
    try {
      const result = await agaveService.v2SearchAgaveSubJobs(searchInput);
      const validatedResult = validateSearchSubJobsResult(result);
      if (!validatedResult) {
        throw new Error('Invalid search subjobs result structure');
      }
      return { ...validatedResult, dropdownId, serverOrdered: serverOrdersCoding(getState()) };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  },
  {
    // Add condition function to prevent duplicate requests
    condition: (input, { getState }) => {
      const state = getState() as RootState;
      const agaveState = state?.cards?.agave;

      // Don't dispatch if already searching or if already initialized
      if (
        agaveState?.searchingSubJobs ||
        agaveState?.searchInitialization?.subJobs?.status === 'pending' ||
        agaveState?.searchInitialization?.subJobs?.status === 'initialized'
      ) {
        return false; // This will prevent the thunk from being dispatched
      }

      return true; // Allow the thunk to be dispatched
    },
  }
);

export const loadMoreSubJobs = createAsyncThunk(
  'agave/loadMoreSubJobs',
  async (input: V2SearchAgaveSubJobsQueryVariables, { rejectWithValue, getState }) => {
    try {
      const result = await agaveService.v2SearchAgaveSubJobs(input);
      const validatedResult = validateSearchSubJobsResult(result);
      if (!validatedResult) {
        throw new Error('Invalid load more subjobs result structure');
      }
      return { ...validatedResult, serverOrdered: serverOrdersCoding(getState()) };
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const getCostTypes = createAsyncThunk(
  'agave/getAgaveCostTypes',
  async (input: { paidolId: string; limit?: number }, { rejectWithValue, getState }) => {
    try {
      const result = await agaveService.listAgaveCostTypesByPaidolId({
        input: { paidolId: input.paidolId, limit: input.limit },
      });
      const validatedResult = validateArrayResult(result.items);
      if (!validatedResult) {
        throw new Error('Invalid cost types result structure');
      }
      // SEE-1325: same gap as getCostCodes above — never sorted, which is why
      // the cost type dropdown shows "Speedchain, Labor, Materials, Labor".
      // Same server-sorted gate too.
      const root = getState() as RootState;
      const paidol = root.userCompanies?.selectedPaidol;
      const fallbackSlug = root.cards?.agave?.agaveAccount?.source_system?.slug;
      if (!isServerSortedPaidol(paidol, fallbackSlug)) {
        return validatedResult as AgaveCostType[];
      }
      return sortCodingOptions(validatedResult as AgaveCostType[], {
        // As above — the shared entry, not the coincidentally-equal defaults.
        ...CODING_SORT_FIELDS.costTypes,
        byCode: isCodingSortedByCode(paidol, fallbackSlug),
      }) as AgaveCostType[];
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const getBankAccounts = createAsyncThunk(
  'agave/getBankAccounts',
  async (input: GetAgaveBankAccountsQueryVariables, { rejectWithValue }) => {
    try {
      return asAmplifyType<AgaveBankAccount[]>(await agaveService.getAgaveBankAccounts(input));
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const getAccount = createAsyncThunk(
  'agave/getAgaveAccount',
  async (input: GetAgaveAccountQueryVariables, { rejectWithValue }) => {
    try {
      return asAmplifyType<AgaveAccount>(await agaveService.getAgaveAccount(input));
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const getProjectEmployees = createAsyncThunk(
  'agave/getProjectEmployees',
  async (input: GetAgaveEmployeesQueryVariables, { rejectWithValue }) => {
    try {
      const result = await agaveService.getAgaveEmployees(input);
      const validatedResult = validateArrayResult(result);
      if (!validatedResult) {
        throw new Error('Invalid project employees result structure');
      }
      return validatedResult as AgaveEmployee[];
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

// SEE-857 D3 — Agave account-token / source-system writes flow through the
// admin-only `updatePaidolSettings` Lambda. Callers still pass the legacy
// shape (`{ id, agaveAccountToken, agaveSourceSystemSlug }`); the thunk
// translates `id` → `paidolId` for the new mutation.
export const persistAgaveAccountToken = createAsyncThunk(
  'agave/persistAgaveAccountToken',
  async (
    input: { id: string; agaveAccountToken?: string; agaveSourceSystemSlug?: string },
    { rejectWithValue }
  ) => {
    try {
      const { id, agaveAccountToken, agaveSourceSystemSlug } = input;
      const result = await paidolService.updatePaidolSettings({
        paidolId: id,
        ...(agaveAccountToken !== undefined ? { agaveAccountToken } : {}),
        ...(agaveSourceSystemSlug !== undefined ? { agaveSourceSystemSlug } : {}),
      });
      return result;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : String(error));
    }
  }
);

export const getVendorsByName = createAsyncThunk(
  'agave/getAgaveVendorsByName',
  async (input: GetAgaveVendorByNameQueryVariables, { rejectWithValue }) => {
    try {
      const result = await agaveService.getAgaveVendorByName(input);
      const validatedResult = validateArrayResult(result);
      if (!validatedResult) {
        throw new Error('Invalid vendors by name result structure');
      }
      return validatedResult as AgaveVendor[];
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const getVendors = createAsyncThunk(
  'agave/getVendors',
  async (input: GetAgaveVendorsQueryVariables, { rejectWithValue }) => {
    try {
      const result = await agaveService.getAgaveVendors(input);
      const validatedResult = validateArrayResult(result);
      if (!validatedResult) {
        throw new Error('Invalid vendors result structure');
      }
      return validatedResult as AgaveVendor[];
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const getAPInvoiceByFilterSageIntacct = createAsyncThunk(
  'agave/getAPInvoiceByFilterSageIntacct',
  async (input: GetAgaveAPInvoiceByFilterSageIntacctQueryVariables, { rejectWithValue }) => {
    try {
      const result = await agaveService.getAgaveAPInvoiceByFilterSageIntacct(input);
      const validatedResult = validateArrayResult(result);
      if (!validatedResult) {
        throw new Error('Invalid AP invoice by filter Sage Intacct result structure');
      }
      return validatedResult as AgaveAPInvoicesPayload[];
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const createOrUpdateAgaveExpense = createAsyncThunk(
  'agave/createOrUpdateAgaveExpense',
  async (input: AgaveCreateExpenseMutationVariables, { rejectWithValue }) => {
    try {
      return await agaveService.agaveCreateExpense(input);
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const createAgaveAPInvoice = createAsyncThunk(
  'agave/createAgaveAPInvoice',
  async (input: AgaveAPInvoicesCreateExpenseMutationVariables, { rejectWithValue }) => {
    try {
      return await agaveService.agaveAPInvoicesCreateExpense(input);
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const createAgaveJobCost = createAsyncThunk(
  'agave/createAgaveJobCost',
  async (input: AgavePostJobCostMutationVariables, { rejectWithValue }) => {
    try {
      return await agaveService.agavePostJobCost(input);
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const createSageIntacctAPInvoice = createAsyncThunk(
  'agave/createSageIntacctAPInvoice',
  async (input: PostAgavePassThroughMutationVariables, { rejectWithValue }) => {
    try {
      return await agaveService.postAgavePassThrough(input);
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const postAgavePassthrough = createAsyncThunk(
  'agave/postAgavePassthrough',
  async (input: PostAgavePassThroughMutationVariables, { rejectWithValue }) => {
    try {
      return await agaveService.postAgavePassThrough(input);
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const postAgaveResolverMutation = createAsyncThunk(
  'agave/agaveResolverMutation',
  async (input: AgaveResolverMutationMutationVariables, { rejectWithValue }) => {
    try {
      return await agaveService.agaveResolverMutation(input);
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

// TODO: for credit card credit, ideally should switch to returning the correct type from the backend, but was rushed to get this working and fixing and redeploy will be too slow
export const createCreditCardCreditQBO = createAsyncThunk(
  'agave/createCreditCardCreditQBO',
  async (input: AgaveResolverMutationMutationVariables, { rejectWithValue }) => {
    try {
      const data = await agaveService.agaveResolverMutation(input);
      if (data && 'body' in data && data.body) {
        return JSON.parse(data.body);
      } else {
        throw new Error('Error creating `Credit Card Credit` in QBO');
      }
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const createSageIntacctVendor = createAsyncThunk(
  'agave/createSageIntacctVendor',

  async (input: PostAgavePassThroughMutationVariables, { rejectWithValue }) => {
    const payload = `<create>
      <VENDOR>
        <VENDORID>SPEEDCHAIN</VENDORID>
        <NAME>SPEEDCHAIN</NAME>
        <DISPLAYCONTACT>
          <PRINTAS>SPEEDCHAIN</PRINTAS>
          <COMPANYNAME>SPEEDCHAIN</COMPANYNAME>
          <PREFIX>Mr</PREFIX>
          <FIRSTNAME>Daniel</FIRSTNAME>
          <LASTNAME>Cage</LASTNAME>
          <INITIAL></INITIAL>
          <PHONE1>470-903-2971</PHONE1>
          <EMAIL1>info@speedchain.com</EMAIL1>
          <EMAIL2></EMAIL2>
          <URL1></URL1>
          <URL2></URL2>
          <MAILADDRESS>
            <ADDRESS1>115 Howell Mill Rd NW</ADDRESS1>
            <ADDRESS2>Ste 360</ADDRESS2>
            <CITY>Atlanta</CITY>
            <STATE>GA</STATE>
            <ZIP>30318</ZIP>
            <COUNTRY>United States</COUNTRY>
          </MAILADDRESS>
        </DISPLAYCONTACT>
      </VENDOR>
    </create>`
      .replace(/>\s+</g, '><')
      .trim();
    input.input.data = payload;
    try {
      const result = await agaveService.postAgavePassThrough(input);
      if (result && 'body' in result) {
        return (result as AgavePassThroughResponse)?.body;
      }
      throw new Error((result as unknown as AgaveError)?.message ?? 'Unknown error');
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const getSageIntacctVendor = createAsyncThunk(
  'agave/getSageIntacctVendor',
  async (input: PostAgavePassThroughMutationVariables, { rejectWithValue }) => {
    const payload = `<readByName>
      <object>VENDOR</object>
      <keys>SPEEDCHAIN</keys>
      <fields>*</fields>
    </readByName>`
      .replace(/>\s+</g, '><')
      .trim();
    input.input.data = payload;
    try {
      const result = await agaveService.postAgavePassThrough(input);
      if (result && 'body' in result) {
        return (result as AgavePassThroughResponse)?.body;
      }
      throw new Error((result as unknown as AgaveError)?.message ?? 'Unknown error');
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const getSageIntacctVendorByQuery = createAsyncThunk(
  'agave/getSageIntacctVendorByQuery',
  async (input: PostAgavePassThroughMutationVariables, { rejectWithValue }) => {
    input.input.data = jsonToXml({
      readByQuery: {
        object: 'VENDOR',
        fields: '*',
        query: "NAME like '%Speedchain%'",
      },
    });
    try {
      const result = await agaveService.postAgavePassThrough(input);
      if (result && 'body' in result) {
        // TODO: since using passthrough, we should parse the body to JSON here rather than in agaveSage.ts:sageIntacctUtil.extractVendorId
        return (result as AgavePassThroughResponse)?.body;
      }
      throw new Error((result as unknown as AgaveError)?.message ?? 'Unknown error');
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const getSageIntacctCreditCards = createAsyncThunk(
  'agave/getSageIntacctCreditCards',
  async (input: PostAgavePassThroughMutationVariables, { rejectWithValue }) => {
    try {
      const result = await agaveService.postAgavePassThrough(input);
      if (result && 'body' in result) {
        const response = result as AgavePassThroughResponse;
        if (response?.body) {
          try {
            const parsedBody = typeof response.body === 'string' ? JSON.parse(response.body) : response.body;
            return parsedBody;
          } catch {
            return null;
          }
        }
        return null;
      }
      throw new Error((result as unknown as AgaveError)?.message ?? 'Unknown error');
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

export const migrateERPEndpointSettings = createAsyncThunk(
  'agave/migrateERPEndpointSettings',
  async (input: MigrateERPEndpointSettingsQueryVariables, { rejectWithValue }) => {
    try {
      return (await agaveService.migrateERPEndpointSettings(input)) as MigrateERPEndpointSettingsPayload;
    } catch (err) {
      return rejectWithValue(err instanceof Error ? err.message : err);
    }
  }
);

// SEE-944: monotonic, session-global counter stamped on agave coding writes.
// transactionsSlice uses it to drop a superseded write whose response lands late
// (e.g. a project's location/department auto-fill resolving after the project was
// removed/changed, which would otherwise re-apply stale dimensions — the flicker).
// Order of stamping = dispatch order, so a removal stamped after an auto-fill wins.
// Lives here next to updateTransactionAgaveClient and is shared by every coding
// write path (handlers in utils.tsx + setCodingTypeForTransaction below).
let agaveCodingWriteCounter = 0;
export const nextAgaveCodingSeq = (): number => (agaveCodingWriteCounter += 1);

export const updateTransactionAgaveClient = createAsyncThunk(
  'overview/transactions/updateTransaction',
  async ({
    id,
    agaveCostType,
    agaveCostCode,
    agaveProject,
    agaveVendor,
    agaveSubJob,
    agaveEquipment,
    agaveEmCostCode,
    agaveEmCostType,
    agaveExpense,
    agaveGlCode,
    agaveLocation,
    agaveDepartment,
    agaveSubaccount,
    agaveEmployee,
    codingType,
    erpBillableItemId,
    erpBillableItemName,
    erpBillable,
    accountingDate,
  }: UpdateTransactionArgs) => {
    const updated = await transactionService.updateTransaction({
      id,
      ...(agaveCostType !== undefined && { agaveCostTypeId: agaveCostType?.id ?? null }),
      ...(agaveCostCode !== undefined && { agaveCostCodeId: agaveCostCode?.id ?? null }),
      ...(agaveProject !== undefined && { agaveProjectId: agaveProject?.id ?? null }),
      ...(agaveVendor !== undefined && { agaveVendorId: agaveVendor?.id ?? null }),
      ...(agaveSubJob !== undefined && { agaveSubJobId: agaveSubJob?.id ?? null }),
      ...(agaveEquipment !== undefined && { agaveEquipmentId: agaveEquipment?.id ?? null }),
      ...(agaveEmCostCode !== undefined && { agaveEmCostCodeId: agaveEmCostCode?.id ?? null }),
      ...(agaveEmCostType !== undefined && { agaveEmCostTypeId: agaveEmCostType?.id ?? null }),
      ...(agaveExpense !== undefined && {
        agaveExpenseId: agaveExpense?.id ?? null,
        agaveExpense: agaveExpense as CreateAgaveExpenseAPInvoiceInput | null,
      }),
      ...(agaveGlCode !== undefined && { agaveGlCodeId: agaveGlCode?.id ?? null }),
      ...(agaveLocation !== undefined && { agaveLocationId: agaveLocation?.id ?? null }),
      ...(agaveDepartment !== undefined && { agaveDepartmentId: agaveDepartment?.id ?? null }),
      ...(agaveSubaccount !== undefined && { agaveSubaccountId: agaveSubaccount?.id ?? null }),
      ...(agaveEmployee !== undefined && { agaveEmployeeId: agaveEmployee?.id ?? null }),
      ...(codingType !== undefined && { codingType }),
      // SEE-1135 — flat passthrough (id + denormalized name); no agaveX?.id mapping.
      ...(erpBillableItemId !== undefined && { erpBillableItemId }),
      ...(erpBillableItemName !== undefined && { erpBillableItemName }),
      ...(erpBillable !== undefined && { erpBillable }),
      ...(accountingDate !== undefined && { accountingDate }),
    });
    return asAmplifyType<HighnoteTransactionEvent>(updated);
  }
);

// SEE-974: Set a transaction's coding type (Equipment / Project / GL) and, when
// switching between Equipment and Project, clear the other group's coding so a
// row never carries both at once (the two are mutually exclusive on a Vista AP
// line). Shared by the table Type cell and the sidesheet type selector so both
// behave identically. GL clears nothing — it leaves any existing coding intact.
// The optimistic applyLocalTransactionUpdates mirrors how handleChangeProjects/
// handleChangeEquipment clear their dependents, so the UI updates instantly and
// updateTransactionAgaveClient persists the nulls.
export const setCodingTypeForTransaction = createAsyncThunk(
  'overview/transactions/setCodingTypeForTransaction',
  async ({ id, codingType }: { id: string; codingType: string }, { dispatch }) => {
    // Empty codingType = unselect/clear: revert the row to untyped. The 'none'
    // override blanks the cell instantly; persisting null makes it stick. Coding
    // values are left untouched.
    if (!codingType) {
      dispatch(setCodingTypeOverride({ id, codingType: 'none' }));
      await dispatch(
        updateTransactionAgaveClient({ id, codingType: null, clientWriteSeq: nextAgaveCodingSeq() })
      );
      return;
    }

    dispatch(setCodingTypeOverride({ id, codingType }));

    // Optimistically clear the OTHER group's coding so a row never carries both
    // (Equipment greys the Project group and vice-versa; GL is permissive and
    // clears nothing). Mirrors handleChangeProjects/handleChangeEquipment.
    const groupClears: Partial<HighnoteTransactionEvent> =
      codingType === 'equipment'
        ? {
            agaveProject: null,
            agaveProjectId: null,
            agaveSubJob: null,
            agaveSubJobId: null,
            agaveCostCode: null,
            agaveCostCodeId: null,
            agaveCostType: null,
            agaveCostTypeId: null,
          }
        : codingType === 'project'
        ? {
            agaveEquipment: null,
            agaveEquipmentId: null,
            agaveEmCostCode: null,
            agaveEmCostCodeId: null,
            agaveEmCostType: null,
            agaveEmCostTypeId: null,
          }
        : {};

    // Persist the explicit pick as an UPPERCASE codingType so the server type
    // filter matches the SELECTED type even when the row has no coding values.
    dispatch(applyLocalTransactionUpdates([{ id, changes: { ...groupClears } }]));
    await dispatch(
      updateTransactionAgaveClient({
        id,
        codingType: codingType.toUpperCase(),
        // Clear the OTHER group as null objects only — updateTransactionAgaveClient
        // maps each `agaveX: null` to `agaveXId: null` (`agaveX?.id ?? null`), so this
        // clears both the denormalized object AND its id on the backend. Keep that
        // derivation; dropping it would leave the *Id fields un-cleared here.
        ...(codingType === 'equipment' && {
          agaveProject: null,
          agaveSubJob: null,
          agaveCostCode: null,
          agaveCostType: null,
        }),
        ...(codingType === 'project' && {
          agaveEquipment: null,
          agaveEmCostCode: null,
          agaveEmCostType: null,
        }),
        clientWriteSeq: nextAgaveCodingSeq(),
      })
    );
  }
);

// SEE-808 / SEE-802: Auto-fill Sage Intacct Department + Location from a project's
// associated dimensions when there is exactly one unique match. Sage Intacct's data
// model is one-to-one in practice; the SEE-628 composite-ID migration left legacy +
// composite rows in the join tables, so we dedupe by the trailing source UUID before
// counting. Returns null per dimension when there are zero or multiple unique matches.
const trailingDimensionId = (id: string): string => {
  const idx = id.lastIndexOf('#');
  return idx === -1 ? id : id.slice(idx + 1);
};

const pickUniqueByTrailingId = <T extends { id: string }>(items: T[]): T | null => {
  if (!items.length) return null;
  const byKey = new Map<string, T>();
  for (const item of items) {
    const key = trailingDimensionId(item.id);
    if (!byKey.has(key)) byKey.set(key, item);
  }
  return byKey.size === 1 ? Array.from(byKey.values())[0] : null;
};

export const fetchProjectAutoFillDimensions = createAsyncThunk<
  { location: AgaveLocation | null; department: AgaveDepartment | null },
  { paidolId: string; projectId: string }
>('agave/fetchProjectAutoFillDimensions', async ({ paidolId, projectId }) => {
  const locationsPromise = agaveService
    .v2SearchAgaveLocations({
      input: { filter: { paidolId, projectId }, limit: 25, nextToken: null },
    })
    .then((result) => (result?.items ?? []) as AgaveLocation[])
    .catch(() => [] as AgaveLocation[]);

  const departmentsPromise = agaveService
    .v2SearchAgaveDepartments({
      input: { filter: { paidolId, projectId }, limit: 25, nextToken: null },
    })
    .then((result) => (result?.items ?? []) as AgaveDepartment[])
    .catch(() => [] as AgaveDepartment[]);

  const [locations, departments] = await Promise.all([locationsPromise, departmentsPromise]);
  return {
    location: pickUniqueByTrailingId(locations),
    department: pickUniqueByTrailingId(departments),
  };
});

function decrementPendingCodingSave(state: AgaveState, id: string): void {
  const next = (state.pendingCodingSaves[id] ?? 1) - 1;
  if (next <= 0) {
    delete state.pendingCodingSaves[id];
  } else {
    state.pendingCodingSaves[id] = next;
  }
}

const agaveSlice = createSlice({
  name: 'agave',
  initialState,
  reducers: {
    resetAgaveSlice: () => initialState,
    setSelectedProjectId: (state, action) => {
      state.selectedProjectId = action.payload;
    },
    setSearchInitialized: (state, action: { payload: { searchType: SearchType } }) => {
      const { searchType } = action.payload;
      state.searchInitialization[searchType as keyof typeof state.searchInitialization].status =
        'initialized';
    },
    setDropdownInitialized: (state, action: { payload: { dropdownId: string; searchType: SearchType } }) => {
      const { dropdownId, searchType } = action.payload;
      const dropdownIds =
        state.searchInitialization[searchType as keyof typeof state.searchInitialization].dropdownIds;
      if (!dropdownIds.includes(dropdownId)) {
        dropdownIds.push(dropdownId);
      }
    },
    clearDropdownInitialization: (state, action) => {
      const { dropdownId } = action.payload;
      for (const key in state.searchInitialization) {
        const dropdownIds =
          state.searchInitialization[key as keyof typeof state.searchInitialization].dropdownIds;
        const index = dropdownIds.indexOf(dropdownId);
        if (index > -1) {
          dropdownIds.splice(index, 1);
        }
      }
    },
    resetSearchInitialization: (state, action) => {
      const { searchType } = action.payload as { searchType: SearchType };
      if (state.searchInitialization[searchType]) {
        state.searchInitialization[searchType].status = 'idle';
      }

      // Also clear the actual search data to prevent stale results from showing
      if (searchType === 'costCodes') {
        state.searchCostCodes = undefined;
      } else if (searchType === 'costTypes') {
        state.searchCostTypes = undefined;
      } else if (searchType === 'projects') {
        state.searchProjects = undefined;
      } else if (searchType === 'glCodes') {
        state.searchGlCodes = undefined;
      } else if (searchType === 'subJobs') {
        state.searchSubJobs = undefined;
      } else if (searchType === 'vendors') {
        state.searchVendors = undefined;
      } else if (searchType === 'locations') {
        state.searchLocations = undefined;
      } else if (searchType === 'departments') {
        state.searchDepartments = undefined;
      } else if (searchType === 'subaccounts') {
        state.searchSubaccounts = undefined;
      } else if (searchType === 'equipment') {
        state.searchEquipment = undefined;
      } else if (searchType === 'emCostCodes') {
        state.searchEmCostCodes = undefined;
      } else if (searchType === 'emCostTypes') {
        state.searchEmCostTypes = undefined;
      } else if (searchType === 'employees') {
        state.searchEmployees = undefined;
      }
    },
  },
  extraReducers: (builder) => {
    // SEE-1384: per-transaction in-flight coding-save counter (see AgaveState).
    builder.addCase(updateTransactionAgaveClient.pending, (state, action) => {
      const { id } = action.meta.arg;
      state.pendingCodingSaves[id] = (state.pendingCodingSaves[id] ?? 0) + 1;
    });
    builder.addCase(updateTransactionAgaveClient.fulfilled, (state, action) => {
      decrementPendingCodingSave(state, action.meta.arg.id);
    });
    builder.addCase(updateTransactionAgaveClient.rejected, (state, action) => {
      decrementPendingCodingSave(state, action.meta.arg.id);
    });

    builder.addCase(persistAgaveAccountToken.fulfilled, (state, action) => {
      const payload = action.payload as { agaveAccountToken?: string | null } | undefined;
      state.accountToken = payload?.agaveAccountToken ?? undefined;
    });

    builder.addCase(fetchLastFinishedSyncByPaidolId.fulfilled, (state, action) => {
      // CDK codegen skips __typename (codegen-cdk.yml `skipTypename: true`), but the
      // Amplify-generated `PaidolAgaveSync` type still requires it. The runtime data
      // is identical — cast through unknown to satisfy the reducer.
      state.lastSync = (action.payload?.sync ?? undefined) as PaidolAgaveSync | undefined;
    });

    builder.addCase(getProjectEmployees.fulfilled, (state, action) => {
      state.projectEmployees = action.payload || [];
    });

    builder.addCase(getAgaveLinkToken.fulfilled, (state, action) => {
      state.linkToken = action.payload!;
    });

    builder.addCase(getAccount.fulfilled, (state, action) => {
      state.agaveAccount = action.payload;
    });

    builder.addCase(getAccount.rejected, (state) => {
      state.agaveAccount = undefined;
    });

    builder.addCase(searchProjects.fulfilled, (state, action) => {
      // `byCode` is transport, not part of the search result — spreading it in
      // would persist a field AgaveState says doesn't exist.
      const { dropdownId, serverOrdered, ...searchResult } = action.payload;
      state.searchProjects = {
        ...searchResult,
        // SEE-1325: match the server's ordering (code_field "number",
        // name_field "fullName"). Sorting by name here regardless of the
        // setting silently undid the code order the resolver just applied.
        items: orderCodingItems(searchResult.items || [], serverOrdered, CODING_SORT_FIELDS.projects),
      };
      state.searchInitialization.projects.status = 'initialized';
      state.searchingProjects = false;
    });

    builder.addCase(searchProjects.pending, (state) => {
      state.searchingProjects = true;
      state.searchInitialization.projects.status = 'pending';
    });

    builder.addCase(searchProjects.rejected, (state) => {
      state.searchingProjects = false;
      state.searchInitialization.projects.status = 'idle';
    });

    builder.addCase(loadMoreProjects.fulfilled, (state, action) => {
      if (action.payload && state.searchProjects) {
        // Only update if we actually got new items
        if (action.payload.items && action.payload.items.length > 0) {
          state.searchProjects = {
            // SEE-1325: honour the toggle here too. Sorting by name on load-more
            // silently undid the code order page 1 arrived in, so scrolling
            // reverted the ordering for opted-in orgs.
            items: orderCodingItems(
              [...state.searchProjects.items, ...action.payload.items],
              action.payload.serverOrdered,
              CODING_SORT_FIELDS.projects
            ),
            nextToken: action.payload.nextToken,
            total: action.payload.total,
          };
        } else {
          // If no new items were returned, keep the current state but update nextToken
          state.searchProjects = {
            ...state.searchProjects,
            nextToken: action.payload.nextToken,
          };
        }
      }
      state.loadingMoreProjects = false;
    });

    builder.addCase(loadMoreProjects.pending, (state) => {
      state.loadingMoreProjects = true;
    });

    builder.addCase(loadMoreProjects.rejected, (state) => {
      state.loadingMoreProjects = false;
    });

    builder.addCase(getCostTypes.fulfilled, (state, action) => {
      state.costTypes = action.payload!;
    });

    builder.addCase(getCostCodes.fulfilled, (state, action) => {
      const isQBDClient = state.agaveAccount?.source_system?.slug === AgaveSourceSystem.QUICKBOOKS_DESKTOP;

      state.projectCostCodes = isQBDClient
        ? action.payload.map((item) => {
            return {
              ...item,
              fullName: showQBDFullName(item),
            };
          })
        : action.payload || [];
    });

    builder.addCase(searchCostCodes.fulfilled, (state, action) => {
      const { dropdownId, serverOrdered, ...searchResult } = action.payload;
      state.searchCostCodes = {
        ...searchResult,
        // SEE-1325: the shared map + toggle, like every other coding entity —
        // a hardcoded field here silently diverges when the map entry moves.
        items: orderCodingItems(searchResult.items || [], serverOrdered, CODING_SORT_FIELDS.costCodes),
      };
      state.searchInitialization.costCodes.status = 'initialized';
      state.searchingCostCodes = false;
    });

    builder.addCase(searchCostCodes.pending, (state) => {
      state.searchingCostCodes = true;
      state.searchInitialization.costCodes.status = 'pending';
    });

    builder.addCase(searchCostCodes.rejected, (state) => {
      state.searchingCostCodes = false;
      state.searchInitialization.costCodes.status = 'idle';
    });

    builder.addCase(loadMoreCostCodes.fulfilled, (state, action) => {
      if (action.payload && state.searchCostCodes) {
        // Only update if we actually got new items
        if (action.payload.items && action.payload.items.length > 0) {
          state.searchCostCodes = {
            items: orderCodingItems(
              [...state.searchCostCodes.items, ...action.payload.items],
              action.payload.serverOrdered,
              CODING_SORT_FIELDS.costCodes
            ),
            nextToken: action.payload.nextToken,
            total: action.payload.total,
          };
        } else {
          // If no new items were returned, keep the current state but update nextToken
          state.searchCostCodes = {
            ...state.searchCostCodes,
            nextToken: action.payload.nextToken,
          };
        }
      }
      state.loadingMoreCostCodes = false;
    });

    builder.addCase(loadMoreCostCodes.pending, (state) => {
      state.loadingMoreCostCodes = true;
    });

    builder.addCase(loadMoreCostCodes.rejected, (state) => {
      state.loadingMoreCostCodes = false;
    });

    builder.addCase(searchCostTypes.fulfilled, (state, action) => {
      const { dropdownId, serverOrdered, ...searchResult } = action.payload;
      state.searchCostTypes = {
        ...searchResult,
        // SEE-1325: the shared map + toggle, like every other coding entity —
        // a hardcoded field here silently diverges when the map entry moves.
        items: orderCodingItems(searchResult.items || [], serverOrdered, CODING_SORT_FIELDS.costTypes),
      };
      state.searchInitialization.costTypes.status = 'initialized';
      state.searchingCostTypes = false;
    });

    builder.addCase(searchCostTypes.pending, (state) => {
      state.searchingCostTypes = true;
      state.searchInitialization.costTypes.status = 'pending';
    });

    builder.addCase(searchCostTypes.rejected, (state) => {
      state.searchingCostTypes = false;
      state.searchInitialization.costTypes.status = 'idle';
    });

    builder.addCase(loadMoreCostTypes.fulfilled, (state, action) => {
      if (action.payload && state.searchCostTypes) {
        // Only update if we actually got new items
        if (action.payload.items && action.payload.items.length > 0) {
          state.searchCostTypes = {
            items: orderCodingItems(
              [...state.searchCostTypes.items, ...action.payload.items],
              action.payload.serverOrdered,
              CODING_SORT_FIELDS.costTypes
            ),
            nextToken: action.payload.nextToken,
            total: action.payload.total,
          };
        } else {
          // If no new items were returned, keep the current state but update nextToken
          state.searchCostTypes = {
            ...state.searchCostTypes,
            nextToken: action.payload.nextToken,
          };
        }
      }
      state.loadingMoreCostTypes = false;
    });

    builder.addCase(loadMoreCostTypes.pending, (state) => {
      state.loadingMoreCostTypes = true;
    });

    builder.addCase(loadMoreCostTypes.rejected, (state) => {
      state.loadingMoreCostTypes = false;
    });

    // SubJobs
    builder.addCase(searchSubJobs.fulfilled, (state, action) => {
      const { dropdownId, serverOrdered, ...searchResult } = action.payload;
      state.searchSubJobs = {
        ...searchResult,
        items: keepServerOrder(searchResult.items || [], serverOrdered, 'fullName'),
      };
      state.searchInitialization.subJobs.status = 'initialized';
      state.searchingSubJobs = false;
    });

    builder.addCase(searchSubJobs.pending, (state) => {
      state.searchingSubJobs = true;
      state.searchInitialization.subJobs.status = 'pending';
    });

    builder.addCase(searchSubJobs.rejected, (state) => {
      state.searchingSubJobs = false;
      state.searchInitialization.subJobs.status = 'idle';
    });

    builder.addCase(loadMoreSubJobs.fulfilled, (state, action) => {
      if (action.payload && state.searchSubJobs) {
        // Only update if we actually got new items
        if (action.payload.items && action.payload.items.length > 0) {
          state.searchSubJobs = {
            items: keepServerOrder(
              [...state.searchSubJobs.items, ...action.payload.items],
              action.payload.serverOrdered,
              'fullName'
            ),
            nextToken: action.payload.nextToken,
            total: action.payload.total,
          };
        } else {
          // If no new items were returned, keep the current state but update nextToken
          state.searchSubJobs = {
            ...state.searchSubJobs,
            nextToken: action.payload.nextToken,
          };
        }
      }
      state.loadingMoreSubJobs = false;
    });

    builder.addCase(loadMoreSubJobs.pending, (state) => {
      state.loadingMoreSubJobs = true;
    });

    builder.addCase(loadMoreSubJobs.rejected, (state) => {
      state.loadingMoreSubJobs = false;
    });

    builder.addCase(getGlCodes.fulfilled, (state, action) => {
      const isQBDClient = state.agaveAccount?.source_system?.slug === AgaveSourceSystem.QUICKBOOKS_DESKTOP;

      state.glCodes = isQBDClient
        ? action.payload.map((item) => {
            return {
              ...item,
              fullName: showQBDFullName(item),
            };
          })
        : action.payload || [];
    });

    builder.addCase(searchGlCodes.fulfilled, (state, action) => {
      // `byCode` is transport — see searchProjects.fulfilled.
      const { dropdownId, serverOrdered, ...searchResult } = action.payload;
      state.searchGlCodes = {
        ...searchResult,
        // SEE-1325: as with projects — same fields the resolver sorts on.
        items: orderCodingItems(searchResult.items || [], serverOrdered, CODING_SORT_FIELDS.glCodes),
      };
      state.searchInitialization.glCodes.status = 'initialized';
      state.searchingGlCodes = false;
    });

    builder.addCase(searchGlCodes.pending, (state) => {
      state.searchingGlCodes = true;
      state.searchInitialization.glCodes.status = 'pending';
    });

    builder.addCase(searchGlCodes.rejected, (state) => {
      state.searchingGlCodes = false;
      state.searchInitialization.glCodes.status = 'idle';
    });

    builder.addCase(loadMoreGlCodes.fulfilled, (state, action) => {
      if (action.payload && state.searchGlCodes) {
        // Only update if we actually got new items
        if (action.payload.items && action.payload.items.length > 0) {
          state.searchGlCodes = {
            // SEE-1325: honour the toggle here too. Sorting by name on load-more
            // silently undid the code order page 1 arrived in, so scrolling
            // reverted the ordering for opted-in orgs.
            items: orderCodingItems(
              [...state.searchGlCodes.items, ...action.payload.items],
              action.payload.serverOrdered,
              CODING_SORT_FIELDS.glCodes
            ),
            nextToken: action.payload.nextToken,
            total: action.payload.total,
          };
        } else {
          // If no new items were returned, keep the current state but update nextToken
          state.searchGlCodes = {
            ...state.searchGlCodes,
            nextToken: action.payload.nextToken,
          };
        }
      }
      state.loadingMoreGlCodes = false;
    });

    builder.addCase(loadMoreGlCodes.pending, (state) => {
      state.loadingMoreGlCodes = true;
    });

    builder.addCase(loadMoreGlCodes.rejected, (state) => {
      state.loadingMoreGlCodes = false;
    });

    builder.addCase(getGlCodesByCosting.pending, (state) => {
      state.loadingGlCodesByCosting = true;
    });

    builder.addCase(getGlCodesByCosting.fulfilled, (state, action) => {
      const { key, items } = action.payload;
      state.glCodesByCosting = {
        ...state.glCodesByCosting,
        [key]: items,
      };
      state.loadingGlCodesByCosting = false;
    });

    builder.addCase(getGlCodesByCosting.rejected, (state) => {
      state.loadingGlCodesByCosting = false;
    });

    builder.addCase(searchVendors.fulfilled, (state, action) => {
      const { dropdownId, serverOrdered, ...searchResult } = action.payload;
      state.searchVendors = {
        ...searchResult,
        // SEE-1325: the shared map + toggle, like every other coding entity —
        // a hardcoded field here silently diverges when the map entry moves.
        items: orderCodingItems(searchResult.items || [], serverOrdered, CODING_SORT_FIELDS.vendors),
      };
      state.searchInitialization.vendors.status = 'initialized';
      state.searchingVendors = false;
    });

    builder.addCase(searchVendors.pending, (state) => {
      state.searchingVendors = true;
      state.searchInitialization.vendors.status = 'pending';
    });

    builder.addCase(searchVendors.rejected, (state) => {
      state.searchingVendors = false;
      state.searchInitialization.vendors.status = 'idle';
    });

    builder.addCase(loadMoreVendors.fulfilled, (state, action) => {
      if (action.payload && state.searchVendors) {
        // Only update if we actually got new items
        if (action.payload.items && action.payload.items.length > 0) {
          state.searchVendors = {
            items: orderCodingItems(
              [...state.searchVendors.items, ...action.payload.items],
              action.payload.serverOrdered,
              CODING_SORT_FIELDS.vendors
            ),
            nextToken: action.payload.nextToken,
            total: action.payload.total,
          };
        } else {
          // If no new items were returned, keep the current state but update nextToken
          state.searchVendors = {
            ...state.searchVendors,
            nextToken: action.payload.nextToken,
          };
        }
      }
      state.loadingMoreVendors = false;
    });

    builder.addCase(loadMoreVendors.pending, (state) => {
      state.loadingMoreVendors = true;
    });

    builder.addCase(loadMoreVendors.rejected, (state) => {
      state.loadingMoreVendors = false;
    });

    builder.addCase(searchLocations.fulfilled, (state, action) => {
      const { dropdownId, serverOrdered, ...searchResult } = action.payload;
      state.searchLocations = {
        ...searchResult,
        items: keepServerOrder(searchResult.items || [], serverOrdered, 'name'),
      };
      state.searchInitialization.locations.status = 'initialized';
      state.searchingLocations = false;
    });

    builder.addCase(searchLocations.pending, (state) => {
      state.searchingLocations = true;
      state.searchInitialization.locations.status = 'pending';
    });

    builder.addCase(searchLocations.rejected, (state) => {
      state.searchingLocations = false;
      state.searchInitialization.locations.status = 'idle';
    });

    builder.addCase(loadMoreLocations.fulfilled, (state, action) => {
      if (action.payload && state.searchLocations) {
        if (action.payload.items && action.payload.items.length > 0) {
          state.searchLocations = {
            items: keepServerOrder(
              [...state.searchLocations.items, ...action.payload.items],
              action.payload.serverOrdered,
              'name'
            ),
            nextToken: action.payload.nextToken,
            total: action.payload.total,
          };
        } else {
          state.searchLocations = {
            ...state.searchLocations,
            nextToken: action.payload.nextToken,
          };
        }
      }
      state.loadingMoreLocations = false;
    });

    builder.addCase(loadMoreLocations.pending, (state) => {
      state.loadingMoreLocations = true;
    });

    builder.addCase(loadMoreLocations.rejected, (state) => {
      state.loadingMoreLocations = false;
    });

    builder.addCase(searchDepartments.fulfilled, (state, action) => {
      const { dropdownId, serverOrdered, ...searchResult } = action.payload;
      state.searchDepartments = {
        ...searchResult,
        items: keepServerOrder(searchResult.items || [], serverOrdered, 'name'),
      };
      state.searchInitialization.departments.status = 'initialized';
      state.searchingDepartments = false;
    });

    builder.addCase(searchDepartments.pending, (state) => {
      state.searchingDepartments = true;
      state.searchInitialization.departments.status = 'pending';
    });

    builder.addCase(searchDepartments.rejected, (state) => {
      state.searchingDepartments = false;
      state.searchInitialization.departments.status = 'idle';
    });

    builder.addCase(loadMoreDepartments.fulfilled, (state, action) => {
      if (action.payload && state.searchDepartments) {
        if (action.payload.items && action.payload.items.length > 0) {
          state.searchDepartments = {
            items: keepServerOrder(
              [...state.searchDepartments.items, ...action.payload.items],
              action.payload.serverOrdered,
              'name'
            ),
            nextToken: action.payload.nextToken,
            total: action.payload.total,
          };
        } else {
          state.searchDepartments = {
            ...state.searchDepartments,
            nextToken: action.payload.nextToken,
          };
        }
      }
      state.loadingMoreDepartments = false;
    });

    builder.addCase(loadMoreDepartments.pending, (state) => {
      state.loadingMoreDepartments = true;
    });

    builder.addCase(loadMoreDepartments.rejected, (state) => {
      state.loadingMoreDepartments = false;
    });

    builder.addCase(searchSubaccounts.fulfilled, (state, action) => {
      const { dropdownId, serverOrdered, ...searchResult } = action.payload;
      state.searchSubaccounts = {
        ...searchResult,
        items: keepServerOrder(searchResult.items || [], serverOrdered, 'name'),
      };
      state.searchInitialization.subaccounts.status = 'initialized';
      state.searchingSubaccounts = false;
    });

    builder.addCase(searchSubaccounts.pending, (state) => {
      state.searchingSubaccounts = true;
      state.searchInitialization.subaccounts.status = 'pending';
    });

    builder.addCase(searchSubaccounts.rejected, (state) => {
      state.searchingSubaccounts = false;
      state.searchInitialization.subaccounts.status = 'idle';
    });

    builder.addCase(loadMoreSubaccounts.fulfilled, (state, action) => {
      if (action.payload && state.searchSubaccounts) {
        if (action.payload.items && action.payload.items.length > 0) {
          state.searchSubaccounts = {
            items: keepServerOrder(
              [...state.searchSubaccounts.items, ...action.payload.items],
              action.payload.serverOrdered,
              'name'
            ),
            nextToken: action.payload.nextToken,
            total: action.payload.total,
          };
        } else {
          state.searchSubaccounts = {
            ...state.searchSubaccounts,
            nextToken: action.payload.nextToken,
          };
        }
      }
      state.loadingMoreSubaccounts = false;
    });

    builder.addCase(loadMoreSubaccounts.pending, (state) => {
      state.loadingMoreSubaccounts = true;
    });

    builder.addCase(loadMoreSubaccounts.rejected, (state) => {
      state.loadingMoreSubaccounts = false;
    });

    // SEE-1325: equipment follows the toggle like every other coding entity —
    // name order off, code order on. (SEE-1435 originally pinned it to code
    // because many records have no description; sortCodingOptions now puts
    // blank values LAST, matching the backend's name_sort_key, so a name sort
    // no longer floats those rows to the top.)
    builder.addCase(searchEquipment.fulfilled, (state, action) => {
      const { dropdownId, serverOrdered, ...searchResult } = action.payload;
      state.searchEquipment = {
        ...searchResult,
        items: orderCodingItems(searchResult.items || [], serverOrdered, CODING_SORT_FIELDS.equipment),
      };
      state.searchInitialization.equipment.status = 'initialized';
      state.searchingEquipment = false;
    });

    builder.addCase(searchEquipment.pending, (state) => {
      state.searchingEquipment = true;
      state.searchInitialization.equipment.status = 'pending';
    });

    builder.addCase(searchEquipment.rejected, (state) => {
      state.searchingEquipment = false;
      state.searchInitialization.equipment.status = 'idle';
    });

    builder.addCase(loadMoreEquipment.fulfilled, (state, action) => {
      if (action.payload && state.searchEquipment) {
        if (action.payload.items && action.payload.items.length > 0) {
          state.searchEquipment = {
            items: orderCodingItems(
              [...state.searchEquipment.items, ...action.payload.items],
              action.payload.serverOrdered,
              CODING_SORT_FIELDS.equipment
            ),
            nextToken: action.payload.nextToken,
            total: action.payload.total,
          };
        } else {
          state.searchEquipment = {
            ...state.searchEquipment,
            nextToken: action.payload.nextToken,
          };
        }
      }
      state.loadingMoreEquipment = false;
    });

    builder.addCase(loadMoreEquipment.pending, (state) => {
      state.loadingMoreEquipment = true;
    });

    builder.addCase(loadMoreEquipment.rejected, (state) => {
      state.loadingMoreEquipment = false;
    });

    builder.addCase(searchEmployees.fulfilled, (state, action) => {
      const { dropdownId, serverOrdered, ...searchResult } = action.payload;
      state.searchEmployees = {
        ...searchResult,
        items: keepServerOrder(searchResult.items || [], serverOrdered, 'name'),
      };
      state.searchInitialization.employees.status = 'initialized';
      state.searchingEmployees = false;
    });

    builder.addCase(searchEmployees.pending, (state) => {
      state.searchingEmployees = true;
      state.searchInitialization.employees.status = 'pending';
    });

    builder.addCase(searchEmployees.rejected, (state) => {
      state.searchingEmployees = false;
      state.searchInitialization.employees.status = 'idle';
    });

    builder.addCase(loadMoreEmployees.fulfilled, (state, action) => {
      if (action.payload && state.searchEmployees) {
        if (action.payload.items && action.payload.items.length > 0) {
          state.searchEmployees = {
            items: keepServerOrder(
              [...state.searchEmployees.items, ...action.payload.items],
              action.payload.serverOrdered,
              'name'
            ),
            nextToken: action.payload.nextToken,
            total: action.payload.total,
          };
        } else {
          state.searchEmployees = {
            ...state.searchEmployees,
            nextToken: action.payload.nextToken,
          };
        }
      }
      state.loadingMoreEmployees = false;
    });

    builder.addCase(loadMoreEmployees.pending, (state) => {
      state.loadingMoreEmployees = true;
    });

    builder.addCase(loadMoreEmployees.rejected, (state) => {
      state.loadingMoreEmployees = false;
    });

    builder.addCase(getVendors.fulfilled, (state, action) => {
      state.vendors = action.payload || [];
    });

    // getPersistedAgaveAccountToken returns a string (agaveAccountToken from Paidol)
    builder.addCase(getPersistedAgaveAccountToken.fulfilled, (state, action) => {
      state.accountToken = action.payload ?? undefined;
    });

    // getAgaveAccountToken returns full AgaveAccountToken object from Agave API
    builder.addCase(getAgaveAccountToken.fulfilled, (state, action) => {
      state.accountToken = action.payload?.account_token ?? undefined;
    });

    builder.addMatcher(
      isAnyOf(
        getAgaveLinkToken.pending,
        getAgaveAccountToken.pending,
        getCostCodes.pending,
        getCostTypes.pending,
        createOrUpdateAgaveExpense.pending,
        getVendorsByName.pending,
        getVendors.pending,
        getGlCodes.pending,
        getAPInvoiceByFilterSageIntacct.pending
      ),
      (state) => {
        state.agaveLoading = true;
      }
    );

    builder.addMatcher(
      isAnyOf(
        getAgaveLinkToken.fulfilled,
        getAgaveAccountToken.fulfilled,
        getProjectEmployees.fulfilled,
        getProjectEmployees.rejected,
        getCostCodes.fulfilled,
        getCostCodes.rejected,
        getCostTypes.rejected,
        getCostTypes.fulfilled,
        createOrUpdateAgaveExpense.fulfilled,
        createOrUpdateAgaveExpense.rejected,
        getVendorsByName.fulfilled,
        getVendorsByName.rejected,
        getVendors.fulfilled,
        getVendors.rejected,
        getGlCodes.fulfilled,
        getGlCodes.rejected,
        getAPInvoiceByFilterSageIntacct.fulfilled,
        getAPInvoiceByFilterSageIntacct.rejected
      ),
      (state) => {
        state.agaveLoading = false;
      }
    );
  },
});

export const {
  resetAgaveSlice,
  setSelectedProjectId,
  setSearchInitialized,
  setDropdownInitialized,
  clearDropdownInitialization,
  resetSearchInitialization,
} = agaveSlice.actions;

export const selectAgaveSlice = (state: RootState): AgaveState => state?.cards?.agave ?? initialState;

export default agaveSlice.reducer;
